// goalTemplates.js — THE goal template list. One definition, two views:
// the Add Goal modal's gallery shows every entry grouped by life area, and
// onboarding shows the `onboarding: true` subset (sorted by focus pillar in
// the modal code). Merged 2026-09-14 from the modal's inline chip list and
// the onboarding QUICK_GOALS after the two drifted (docs/
// ADD-GOAL-REDESIGN-REPORT.md): the modal's "Savings fund" was manual while
// onboarding's savings chips bound to the live balance — same intent,
// different honesty, depending on which door you entered. Never fork this
// list again.
//
// Resolved drift, recorded:
// - "Savings fund" merged INTO "Save $5,000" (savings-bound — the honest
//   version wins). "Weight loss" merged into "Lose 20 lbs"; "Books read"
//   into "Read 12 books".
// - "Workout streak" REMOVED: a streak template contradicted the shape
//   picker's own no-streak-shape rule ("habits own streaks"). The gym habit
//   template IS the workout streak; the recurring shape keeps its "add it
//   as a habit instead" offer.
// - "Workouts a week" added as a bound count template. Its source_config is
//   {} — EMPTY, matching production: the count shape reads period_target
//   only. Do not invent a metric key for it.
//
// Field notes:
// - `onboarding` titles are LOAD-BEARING: persistOnboarding and the
//   onboarding goal step match chips by exact title string. Renaming one
//   here without updating those lookups silently downgrades the chip to a
//   free-text manual goal.
// - Bound templates carry the FULL binding (source_type + source_config);
//   templates are the only binding mechanism — free text is never inferred
//   into a binding (parity P3).
// - `screenTime: true` entries route through the native flow at save
//   (authorization → picker → arm) — the draft's number is HOURS, converted
//   to minutes by addGoal's screen-time block.
// - `moneyStart: true` entries offer the one optional "already saved
//   something?" line — the only place production shows real use of a
//   starting value.

export const TEMPLATE_GROUPS = [
  ["money", "Money"],
  ["fitness", "Fitness"],
  ["mind", "Mind & personal"],
  ["screen", "Screen time"],
];

export const GOAL_TEMPLATES = [
  // ── Money ────────────────────────────────────────────────────────────────
  { title: "Save $5,000", hint: "$5,000", group: "money", category: "finance",
    target_value: "5000", unit: "$", source_type: "savings",
    onboarding: true, moneyStart: true },
  { title: "Build $1k emergency fund", hint: "$1,000", group: "money", category: "finance",
    target_value: "1000", unit: "$", source_type: "savings",
    onboarding: true, moneyStart: true },
  // Auto-tracked when a bank is connected: source_type 'credit_payoff' binds
  // to the live sum of Plaid credit-account balances (goal-progress.js). The
  // target defaults to the live balance at bind (captured as start_value),
  // editable down to a partial payoff. With no bank it stays a manual goal.
  { title: "Pay off credit cards", hint: "your balance", group: "money", category: "finance",
    target_value: "", unit: "$", targetHint: "your total card balance",
    source_type: "credit_payoff",
    onboarding: true, moneyStart: true },
  { title: "Eating out", hint: "$300/mo", group: "money", category: "finance",
    target_value: "300", unit: "$", source_type: "plaid_spend",
    source_config: { category: "FOOD_AND_DRINK", detailed_categories: ["FOOD_AND_DRINK_RESTAURANT", "FOOD_AND_DRINK_FAST_FOOD", "FOOD_AND_DRINK_COFFEE", "FOOD_AND_DRINK_OTHER_FOOD_AND_DRINK"] },
    period: "month", period_target: "300" },
  { title: "Groceries", hint: "$500/mo", group: "money", category: "finance",
    target_value: "500", unit: "$", source_type: "plaid_spend",
    source_config: { category: "FOOD_AND_DRINK", detailed_categories: ["FOOD_AND_DRINK_GROCERIES"] },
    period: "month", period_target: "500" },
  { title: "Shopping", hint: "$200/mo", group: "money", category: "finance",
    target_value: "200", unit: "$", source_type: "plaid_spend",
    source_config: { category: "GENERAL_MERCHANDISE" },
    period: "month", period_target: "200" },
  { title: "Gas & transport", hint: "$250/mo", group: "money", category: "finance",
    target_value: "250", unit: "$", source_type: "plaid_spend",
    source_config: { category: "TRANSPORTATION" },
    period: "month", period_target: "250" },
  { title: "Entertainment", hint: "$100/mo", group: "money", category: "finance",
    target_value: "100", unit: "$", source_type: "plaid_spend",
    source_config: { category: "ENTERTAINMENT" },
    period: "month", period_target: "100" },
  { title: "Travel", hint: "$300/mo", group: "money", category: "finance",
    target_value: "300", unit: "$", source_type: "plaid_spend",
    source_config: { category: "TRAVEL" },
    period: "month", period_target: "300" },

  // ── Fitness ──────────────────────────────────────────────────────────────
  { title: "Weekly miles", hint: "10 mi/wk", group: "fitness", category: "fitness",
    target_value: "10", unit: "mi", source_type: "workout",
    source_config: { metric: "weekly_distance", unit: "mi" },
    period: "week", period_target: "10" },
  { title: "Workouts a week", hint: "4x/wk", group: "fitness", category: "fitness",
    target_value: "4", unit: "", source_type: "workout",
    source_config: {}, // EMPTY on purpose — count shape reads period_target only (production shape)
    period: "week", period_target: "4" },
  { title: "Lose 20 lbs", hint: "20 lbs", group: "fitness", category: "fitness",
    target_value: "20", unit: "lbs", onboarding: true },
  { title: "Run a 5K", hint: "5 km", group: "fitness", category: "fitness",
    target_value: "5", unit: "km", onboarding: true },

  // ── Mind & personal ──────────────────────────────────────────────────────
  { title: "30 days alcohol-free", hint: "30 days", group: "mind", category: "wellness",
    target_value: "30", unit: "days", onboarding: true },
  { title: "Meditate 100 days", hint: "100 days", group: "mind", category: "wellness",
    target_value: "100", unit: "days", onboarding: true },
  { title: "Read 12 books", hint: "12 books", group: "mind", category: "personal",
    target_value: "12", unit: "books", onboarding: true },

  // ── Screen time ──────────────────────────────────────────────────────────
  // Native-flow template: category health forced (the card lives on the
  // Health tab; the cron signal's pillar tag is a separate axis). The
  // number is HOURS in the draft; addGoal converts to minutes.
  { title: "Screen time limit", hint: "under 4h/day", group: "screen", category: "health",
    target_value: "4", unit: "min", period: "day", period_target: "4",
    screenTime: true },
];

export const ONBOARDING_GOAL_TEMPLATES = GOAL_TEMPLATES.filter(t => t.onboarding);

// Custom-path category inference — category is never ASKED anymore, but it
// must still be SET: assembleSignals (api/notify-cron.js) maps it to the
// signal's pillar tag (finance → money, fitness/health → fitness, else
// wellness), and it routes which tab renders the card. Bounded-unit input
// makes this inference honest: units come from a picker, not free text.
// Screen-time cap conversion — THE one pair, used by create (confirm
// screen) and edit alike. The user always sees HOURS (the template's own
// language: "under 4h/day"); minutes exist only at the storage layer
// (period_target / cap_minutes). 30 min ↔ 0.5, 240 ↔ 4, 270 ↔ 4.5.
export const stHoursToMinutes = (hours) => Math.round(parseFloat(hours) * 60);
export const stMinutesToHours = (minutes) => parseFloat((Number(minutes) / 60).toFixed(2));

export function inferGoalCategory(unit) {
  const u = (unit || "").toLowerCase();
  if (u === "lbs" || u === "mi" || u === "km") return "fitness";
  if (u === "$" || u === "%") return "finance";
  return "personal";
}

// ── CUSTOM GOAL AUTO-TRACK (2026-10-20) ──────────────────────────────────────
// The custom form's unit picker, and which (shape, unit) pairs map to an
// EXISTING resolver binding. Same bindings the templates carry, so the card,
// the cron and Kai's context resolve a custom goal exactly like a template one:
//   $ + cap        → plaid_spend, monthly, scope 'everyday' (purchases only;
//                    include_bills adds rent/utilities/insurance; optional
//                    category = one of SPEND_CATEGORY_FILTERS)
//   $ + target     → savings (account balance)
//   mi + recurring → workout weekly_distance
//   steps + recurring → workout weekly_steps
//   workouts + recurring → workout count (linked to the Gym habit, goalLink.js)
//   hours + cap    → screen_time daily cap (native only)
// Anything else is manual ("Manual: you update this"). Returns null for manual.
export const CUSTOM_GOAL_UNITS = ["$", "lbs", "mi", "km", "steps", "workouts", "hours", "days", "books", "%", "count"];

// Category filters for a custom spend cap — the template configs, by key.
export const SPEND_CATEGORY_FILTERS = [
  { key: "eating_out", label: "Eating out", config: GOAL_TEMPLATES.find(t => t.title === "Eating out").source_config },
  { key: "groceries", label: "Groceries", config: GOAL_TEMPLATES.find(t => t.title === "Groceries").source_config },
  { key: "shopping", label: "Shopping", config: { category: "GENERAL_MERCHANDISE" } },
  { key: "transport", label: "Gas & transport", config: { category: "TRANSPORTATION" } },
  { key: "entertainment", label: "Entertainment", config: { category: "ENTERTAINMENT" } },
  { key: "travel", label: "Travel", config: { category: "TRAVEL" } },
];

export function customGoalBinding({ shape = "target", unit = "", includeBills = false, spendFilter = null, native = true } = {}) {
  const u = unit === "count" ? "" : unit;
  if (u === "$" && shape === "cap") {
    const f = SPEND_CATEGORY_FILTERS.find(x => x.key === spendFilter);
    return {
      source_type: "plaid_spend", period: "month", unit: "$", category: "finance",
      source_config: { scope: "everyday", ...(includeBills ? { include_bills: true } : {}), ...(f ? f.config : {}) },
      describe: `Tracks automatically from your bank: ${f ? f.label.toLowerCase() : "everyday spending"}${includeBills ? " plus bills" : ""} this month${includeBills ? "" : " (rent, bills and transfers left out)"}.`,
    };
  }
  if (u === "$" && shape === "target") {
    return { source_type: "savings", period: null, unit: "$", category: "finance", source_config: null,
      describe: "Tracks automatically from your savings account balance." };
  }
  if (shape === "recurring" && u === "mi") {
    return { source_type: "workout", period: "week", unit: "mi", category: "fitness", source_config: { metric: "weekly_distance", unit: "mi" },
      describe: "Tracks automatically: daily distance from Apple Health, plus workouts you log." };
  }
  if (shape === "recurring" && u === "steps") {
    return { source_type: "workout", period: "week", unit: "steps", category: "fitness", source_config: { metric: "weekly_steps" },
      describe: "Tracks automatically from your Apple Health steps." };
  }
  if (shape === "recurring" && u === "workouts") {
    return { source_type: "workout", period: "week", unit: "", category: "fitness", source_config: {},
      describe: "Tracks automatically from your workouts and Gym habit logs." };
  }
  if (shape === "cap" && u === "hours" && native) {
    return { screen_time_bind: true, period: "day", unit: "min", category: "health", source_config: null,
      describe: "Tracks automatically from Screen Time on this iPhone, per day. You'll pick the apps and approve access when you save." };
  }
  return null;
}
